import type { ApiService } from '@/services/api'

export interface ThumbnailGenerationOptions {
  apiService: ApiService
  updateSelectionWithRetry: (selectionId: string, data: any) => Promise<void>
  refreshGallery: (documentId: string) => void
}

export class ThumbnailGenerator {
  protected apiService: ApiService
  protected updateSelectionWithRetry: (selectionId: string, data: any) => Promise<void>
  protected refreshGallery: (documentId: string) => void
  
  // Thumbnail generation queue to prevent concurrency issues
  private thumbnailQueue: Array<{
    selectionId: string,
    documentId: string,
    resolve: (data: string) => void,
    reject: (error: any) => void
  }> = []
  private isProcessingThumbnails: boolean = false

  constructor(options: ThumbnailGenerationOptions) {
    this.apiService = options.apiService
    this.updateSelectionWithRetry = options.updateSelectionWithRetry
    this.refreshGallery = options.refreshGallery
  }

  // Queue-based thumbnail generation (inherited from PDF system)
  public queueThumbnailGeneration(selectionId: string, documentId: string): void {
    console.log('📋 Queuing thumbnail generation for selection:', selectionId)
    
    const promise = new Promise<string>((resolve, reject) => {
      this.thumbnailQueue.push({
        selectionId,
        documentId,
        resolve,
        reject
      })
    })
    
    // Start processing if not already running
    if (!this.isProcessingThumbnails) {
      this.processThumbnailQueue()
    }
    
    // Handle the promise (don't await to avoid blocking)
    promise.catch(error => {
      console.warn('⚠️ Thumbnail generation failed, but selection was created successfully:', error)
    })
  }
  
  private async processThumbnailQueue(): Promise<void> {
    if (this.isProcessingThumbnails || this.thumbnailQueue.length === 0) {
      return
    }
    
    this.isProcessingThumbnails = true
    console.log(`🔄 Processing thumbnail queue: ${this.thumbnailQueue.length} items`)
    
    while (this.thumbnailQueue.length > 0) {
      const item = this.thumbnailQueue.shift()!
      const { selectionId, documentId, resolve, reject } = item
      
      try {
        console.log(`📸 Generating thumbnail for selection: ${selectionId}`)
        const thumbnailData = await this.generateThumbnail(selectionId, documentId)
        resolve(thumbnailData)
        
        // Small delay between thumbnails to avoid overwhelming the system
        await new Promise(resolve => setTimeout(resolve, 100))
        
      } catch (error) {
        console.error(`❌ Failed to generate thumbnail for selection ${selectionId}:`, error)
        reject(error)
      }
    }
    
    this.isProcessingThumbnails = false
    console.log('✅ Finished processing thumbnail queue')
  }
  
  protected async generateThumbnail(_selectionId: string, _documentId: string): Promise<string> {
    // This is a base class - subclasses will implement the actual generation
    throw new Error('generateThumbnail must be implemented by subclass')
  }
}

// PDF Thumbnail Generator (inherits from base)
// TODO: Implement PDF thumbnail generation using shared system
// export class PDFThumbnailGenerator extends ThumbnailGenerator { ... }

// Image Thumbnail Generator (inherits from base)
export class ImageThumbnailGenerator extends ThumbnailGenerator {
  private appState: any
  
  constructor(options: ThumbnailGenerationOptions, appState: any) {
    super(options)
    this.appState = appState
  }
  
  protected async generateThumbnail(selectionId: string, documentId: string): Promise<string> {
    // Get selection details
    const currentProject = this.appState.getCurrentProject()
    if (!currentProject) {
      throw new Error('No current project found')
    }
    
    const response = await this.apiService.listProjectSelections(currentProject.id)
    const selections = response.selections || []
    const selection = selections.find((s: any) => s.id === selectionId)
    
    if (!selection) {
      throw new Error('Selection not found')
    }
    
    // Find the image element
    const imageElement = document.querySelector('#image-display') as HTMLImageElement
    if (!imageElement) {
      throw new Error('Image element not found for thumbnail generation')
    }
    
    // Create canvas for thumbnail
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Could not get canvas context')
    }
    
    // Get current scale
    const scale = parseFloat(imageElement.dataset.currentScale || '1')
    
    // Calculate canvas coordinates
    const canvasX = selection.x * scale
    const canvasY = selection.y * scale
    const canvasWidth = selection.width * scale
    const canvasHeight = selection.height * scale
    
    // Set canvas size
    canvas.width = canvasWidth
    canvas.height = canvasHeight
    
    // Draw the selected region
    ctx.drawImage(
      imageElement,
      canvasX, canvasY, canvasWidth, canvasHeight,  // Source region
      0, 0, canvasWidth, canvasHeight              // Destination region
    )
    
    // Convert to base64
    const thumbnailData = canvas.toDataURL('image/png', 0.8)
    
    // Update selection with thumbnail
    await this.updateSelectionWithRetry(selectionId, { thumbnail_data: thumbnailData })
    
    // Refresh gallery
    setTimeout(() => {
      this.refreshGallery(documentId)
    }, 200)
    
    console.log('✅ Image thumbnail generated successfully')
    return thumbnailData
  }
}
